/**
 * Quiz Ápice — motor de recomendação de rotina capilar.
 *
 * A recomendação roda em DOIS EIXOS, porque as linhas da Ápice se cruzam:
 *   BASE    — a rotina, escolhida pela curvatura (Nutri Waves / Cachos / Crespo Power)
 *   BOOSTER — a condição a tratar, que sobrepõe a base em passos específicos
 *             (PH Control / África Baobá / Scalp Care / Mirrafix)
 *
 * Uma cacheada porosa precisa de Cachos E PH Control — por isso não existe
 * "um perfil, uma linha". A tabela abaixo é determinística e auditável: o time
 * de CRO consegue ler a regra sem ler o código.
 *
 * Todos os produtos já estão renderizados pelo Liquid e escondidos. Este módulo
 * só decide o que revelar e em que ordem — nenhum request, nenhum JSON.
 *
 * @module goshop-quiz
 */

import { Component } from '@theme/component';
import { CartUpdateEvent, CartErrorEvent } from '@theme/events';
import { toMajorUnits } from '@theme/analytics-schema';
import { cartItemsSectionIds } from '@theme/utilities';

const SOURCE_ID = 'goshop-quiz';

/** Todas as chaves de linha válidas. Usada para validar o deep link `?rotina=`. */
const LINE_KEYS = ['waves', 'cachos', 'crespo', 'liso', 'ph', 'africa', 'scalpcare', 'mirra'];

/** Linhas que podem assumir os passos de lavagem e condicionamento. */
const WASH_CAPABLE_BOOSTERS = ['ph', 'africa', 'scalpcare'];

/** Curvatura -> linha base. @type {Record<string, string>} */
const BASE_BY_CURL = {
  ondulado: 'waves',
  cacheado: 'cachos',
  crespo: 'crespo',
  transicao: 'cachos',
  liso: 'liso',
};

/**
 * @typedef {Object} Answers
 * @property {string} [curvatura]
 * @property {string} [lavagem]
 * @property {string} [porosidade]
 * @property {string} [incomodo1]
 * @property {string[]} [incomodo2] - Lista: a pergunta 5 aceita várias dores.
 * @property {string} [quimica]
 * @property {string} [calor] - 'sim' quando usa secador, difusor ou prancha.
 */

/**
 * @typedef {Object} Route
 * @property {string} base - Chave da linha base.
 * @property {string[]} boosters - Até 2 boosters, em ordem de prioridade.
 * @property {string} key - Identificador estável da rota (URL + analytics).
 */

/**
 * Resolve os boosters a partir das respostas.
 *
 * A primeira regra que casar vence; no máximo 2 boosters, sem repetição.
 * A ordem da lista É a ordem de prioridade — não reordenar sem atualizar o spec.
 *
 * @param {Answers} a
 * @returns {string[]}
 */
function resolveBoosters(a) {
  const incomodos = [a.incomodo1, ...(a.incomodo2 ?? [])].filter(Boolean);
  /** @param {string} v */
  const has = (v) => incomodos.includes(v);
  /** @type {string[]} */
  const out = [];

  /** @type {[boolean, string][]} */
  const rules = [
    [has('couro'), 'scalpcare'],
    [a.quimica === 'descoloracao' || a.quimica === 'alisamento', 'africa'],
    [a.porosidade === 'alta' || has('ressecamento'), 'ph'],
    [has('quebra'), 'africa'],
    [has('fixacao'), 'mirra'],
  ];

  for (const [matches, line] of rules) {
    if (matches && !out.includes(line)) out.push(line);
    if (out.length === 2) break;
  }

  return out;
}

/**
 * Monta a rota completa a partir das respostas.
 *
 * @param {Answers} a
 * @returns {Route}
 */
function resolveRoute(a) {
  const base = BASE_BY_CURL[a.curvatura ?? ''] ?? 'cachos';
  /** Chaves de linha aceitas — usado para validar deep links. */
  const boosters = resolveBoosters(a);
  return { base, boosters, key: [base, ...boosters].join('-') };
}

/**
 * Decide, para cada passo, de qual linha sai o produto.
 *
 * @param {Answers} a
 * @param {Route} route
 * @returns {{ step: string, line: string }[]}
 */
function resolveSteps(a, route) {
  const { base, boosters } = route;
  const primary = boosters[0];
  // Só ph/africa/scalpcare têm rotina de lavagem própria. Mirrafix só finaliza.
  const washBooster = WASH_CAPABLE_BOOSTERS.includes(primary ?? '') ? primary : null;

  const steps = [];

  // A técnica de lavagem é restrição, não preferência: No Poo vence o booster.
  if (a.lavagem === 'no_poo') {
    steps.push({ step: 'cowash', line: 'crespo' });
  } else {
    steps.push({ step: 'wash', line: washBooster ?? base });
  }

  steps.push({ step: 'condition', line: washBooster ?? base });

  // O tratamento vai para o booster que AINDA NÃO entrou na rotina. Sem isso, um
  // segundo booster aparece no nome da rota sem contribuir com nenhum produto —
  // a cliente lê "Scalp Care + África Baobá" e não vê nenhum África na lista.
  // Mirrafix é exceção: ela sempre fica com o passo de finalização, então não
  // toma o tratamento de outro booster.
  const finishLine = boosters.includes('mirra') ? 'mirra' : base;
  const spare = boosters.find((b) => b !== washBooster && b !== 'mirra');
  steps.push({ step: 'treat', line: spare ?? primary ?? base });

  steps.push({ step: 'finish', line: finishLine });

  // Quem usa fonte de calor recebe um passo de proteção térmica. Sai da linha
  // que finaliza — é ela que já está no fim da rotina.
  if (a.calor === 'sim') {
    steps.push({ step: 'thermal', line: finishLine });
  }

  if (boosters.includes('scalpcare')) {
    steps.push({ step: 'extra', line: 'scalpcare' });
  }

  return steps;
}

/**
 * Envia um evento pelo adapter de analytics do tema, se houver.
 *
 * @param {string} event
 * @param {Record<string, unknown>} [payload]
 * @param {Record<string, unknown>} [opts]
 */
function track(event, payload = {}, opts = {}) {
  const analytics = /** @type {any} */ (window).gogroupAnalytics;
  if (!analytics?.track) return;
  try {
    analytics.track(event, payload, opts);
  } catch {
    /* analytics nunca derruba o quiz */
  }
}

class GoshopQuiz extends Component {
  requiredRefs = ['panels'];

  /** @type {Answers} */
  #answers = {};

  /** Índice do painel visível. 0 = intro; panels.length-1 = resultado. */
  #index = 0;

  #started = false;

  /** Trava contra click+change disparando dois avanços na mesma seleção. */
  #advancing = false;

  /** Timer do avanço adiado, para poder cancelar. @type {number | undefined} */
  #advanceTimer;

  /** Evita reenviar o e-mail e permite reabilitar o campo no restart. */
  #emailSent = false;

  connectedCallback() {
    super.connectedCallback();

    this.#restore();

    const deepLink = new URLSearchParams(window.location.search).get('rotina');
    if (deepLink) {
      this.#showResultFromKey(deepLink);
    } else {
      this.#show(0);
    }

    // Habilita a animação só DEPOIS do primeiro render — animar a tela inicial
    // faz o quiz parecer piscar quando a seção chega tarde numa loja pesada.
    requestAnimationFrame(() => this.classList.add('gq-live'));
  }

  /** Total de painéis de pergunta (exclui intro e resultado). */
  get #questionCount() {
    return this.#panels.length - 2;
  }

  /** @returns {HTMLElement[]} */
  get #panels() {
    const { panels } = this.refs;
    return /** @type {HTMLElement[]} */ (Array.isArray(panels) ? panels : [panels]).filter(Boolean);
  }

  // ------------------------------------------------------------- navegação

  /** Chamado pelo botão da intro. @type {() => void} */
  start = () => {
    this.#begin();
    this.#submitEmail();
    this.#show(1);
  };

  /** Chamado pelo botão voltar. @type {() => void} */
  back = () => {
    this.#cancelAdvance();
    if (this.#index > 0) this.#show(this.#index - 1);
  };

  /** Chamado pelo "pular" da pergunta opcional. @type {() => void} */
  skip = () => {
    this.#cancelAdvance();
    this.#answers.incomodo2 = [];
    for (const input of this.querySelectorAll('input[name="incomodo2"]')) {
      /** @type {HTMLInputElement} */ (input).checked = false;
    }
    this.#persist();
    this.#advance();
  };

  /** Avança a partir de uma pergunta de múltipla escolha. @type {() => void} */
  next = () => {
    this.#advance();
  };

  /**
   * Pergunta de múltipla escolha: acumula os valores marcados.
   *
   * @param {Event} event
   */
  toggle = (event) => {
    const input = /** @type {HTMLInputElement} */ (event.target);
    if (!input?.name) return;

    this.#begin();
    const panel = input.closest('.gq-panel');
    const marcados = [...(panel?.querySelectorAll(`input[name="${input.name}"]:checked`) ?? [])].map(
      (el) => /** @type {HTMLInputElement} */ (el).value
    );
    this.#answers.incomodo2 = marcados;
    this.#persist();

    track(
      'quiz_answer',
      { params: { question_index: this.#index, question_id: input.name, option_value: marcados.join(',') } },
      { dedupKey: `quiz_answer:${input.name}:${marcados.join(',')}` }
    );
  };

  /**
   * Liga/desliga um passo da rotina antes de adicionar tudo ao carrinho.
   *
   * @param {Event} event
   */
  toggleStep = (event) => {
    const input = /** @type {HTMLInputElement} */ (event.target);
    input.closest('.gq-step')?.classList.toggle('is-out', !input.checked);
    this.#refreshBar();
  };

  /** Chamado por "refazer o quiz". @type {() => void} */
  restart = () => {
    this.#cancelAdvance();
    this.#answers = {};
    this.#persist();
    for (const input of this.querySelectorAll('.gq-opt input')) {
      /** @type {HTMLInputElement} */ (input).checked = false;
    }
    // Passos que a cliente tirou da rotina precisam voltar marcados, senão o
    // próximo resultado nasce com item silenciosamente fora do total.
    for (const keep of this.querySelectorAll('.gq-keep input')) {
      /** @type {HTMLInputElement} */ (keep).checked = true;
    }
    for (const step of this.querySelectorAll('.gq-step.is-out')) {
      step.classList.remove('is-out');
    }
    const email = this.refs.email;
    if (email instanceof HTMLInputElement && !this.#emailSent) email.disabled = false;
    const url = new URL(window.location.href);
    url.searchParams.delete('rotina');
    window.history.replaceState({}, '', url);
    this.#show(0);
  };

  /**
   * Chamado ao responder — ligado a `click` E `change`.
   *
   * Precisa dos dois: `change` não dispara quando a cliente volta uma tela e
   * clica de novo na MESMA opção (o radio já está marcado), e aí o quiz travava.
   * `click` cobre esse caso; `change` cobre a navegação por teclado com as setas,
   * que não gera click. A trava abaixo evita que uma seleção nova, que dispara os
   * dois, avance duas perguntas de uma vez.
   *
   * @param {Event} event
   */
  answer = (event) => {
    const input = /** @type {HTMLInputElement} */ (event.target);
    if (!input?.name) return;
    if (this.#advancing) return;
    this.#advancing = true;

    this.#begin();
    this.#setAnswer(input.name, input.value);
    this.#persist();

    track(
      'quiz_answer',
      {
        params: {
          question_index: this.#index,
          question_id: input.name,
          option_value: input.value,
        },
      },
      { dedupKey: `quiz_answer:${input.name}:${input.value}:${this.#index}` }
    );

    // Feedback visual antes de avançar — trocar de tela instantaneamente
    // faz a cliente duvidar se o clique registrou. O timer fica guardado e
    // preso ao índice de origem: sem isso, tocar "Voltar" nesses 320ms deixava
    // um avanço órfão que jogava a cliente para frente de novo.
    const from = this.#index;
    this.#advanceTimer = window.setTimeout(() => {
      this.#advancing = false;
      this.#advanceTimer = undefined;
      if (this.#index === from) this.#advance();
    }, 320);
  };

  /** Cancela um avanço adiado que ainda não disparou. */
  #cancelAdvance() {
    if (this.#advanceTimer !== undefined) {
      window.clearTimeout(this.#advanceTimer);
      this.#advanceTimer = undefined;
    }
    this.#advancing = false;
  }

  #begin() {
    if (this.#started) return;
    this.#started = true;
    track('quiz_start');
  }

  #advance() {
    if (this.#index >= this.#questionCount) {
      this.#complete();
    } else {
      this.#show(this.#index + 1);
    }
  }

  /**
   * @param {number} index
   */
  #show(index) {
    this.#index = index;
    const panels = this.#panels;

    panels.forEach((panel, i) => {
      panel.hidden = i !== index;
    });

    const isQuestion = index > 0 && index <= this.#questionCount;
    const { progress, counter, back } = this.refs;

    if (progress instanceof HTMLElement) {
      progress.style.width = isQuestion ? `${(index / this.#questionCount) * 100}%` : '0%';
    }
    if (counter instanceof HTMLElement) {
      counter.textContent = isQuestion ? `${index} / ${this.#questionCount}` : '';
    }
    if (back instanceof HTMLElement) {
      back.hidden = index === 0;
    }

    const head = this.refs.head;
    if (head instanceof HTMLElement) head.hidden = !isQuestion;

    this.#applyMirror(panels[index]);

    const bar = this.refs.bar;
    if (bar instanceof HTMLElement && index !== panels.length - 1) bar.hidden = true;

    // Foco na headline da nova pergunta — leitor de tela e teclado precisam saber
    // que a tela mudou.
    const heading = panels[index]?.querySelector('[data-focus]');
    if (heading instanceof HTMLElement) heading.focus({ preventScroll: true });

    if (index > 0) {
      this.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }

  /**
   * Na pergunta 5 ("tem mais alguma coisa?"), esconde a opção já escolhida na 4.
   * Mostrar a mesma opção duas vezes faz a cliente achar que errou o clique.
   *
   * @param {HTMLElement | undefined} panel
   */
  #applyMirror(panel) {
    const source = panel?.dataset.mirror;
    if (!source) return;

    const taken = this.#answers[/** @type {keyof Answers} */ (source)];
    for (const el of panel.querySelectorAll('[data-value]')) {
      const option = /** @type {HTMLElement} */ (el);
      option.hidden = Boolean(taken) && option.dataset.value === taken;
    }
  }

  // -------------------------------------------------------------- resultado

  #complete() {
    const route = resolveRoute(this.#answers);
    this.#renderResult(route, resolveSteps(this.#answers, route));

    const url = new URL(window.location.href);
    url.searchParams.set('rotina', route.key);
    window.history.replaceState({}, '', url);

    track('quiz_complete', {
      params: {
        base_line: route.base,
        boosters: route.boosters.join(',') || 'nenhum',
        route: route.key,
      },
    });

    this.#show(this.#panels.length - 1);
  }

  /**
   * Reconstrói o resultado a partir de uma rota vinda da URL.
   *
   * @param {string} key
   */
  #showResultFromKey(key) {
    // Boosters vêm da URL e precisam ser validados como a base já era: sem isso
    // um link forjado injeta texto arbitrário no badge do resultado e ainda
    // resolve o passo de tratamento para uma linha inexistente.
    const [base, ...rest] = key.split('-').filter(Boolean);
    if (!base || !Object.values(BASE_BY_CURL).includes(base)) {
      this.#show(0);
      return;
    }
    const boosters = rest.filter((b) => LINE_KEYS.includes(b)).slice(0, 2);
    const route = { base, boosters, key: [base, ...boosters].join('-') };
    this.#renderResult(route, resolveSteps(this.#answers, route));
    this.#show(this.#panels.length - 1);
  }

  /**
   * @param {Route} route
   * @param {{ step: string, line: string }[]} steps
   */
  #renderResult(route, steps) {
    const dominant = route.boosters[0] ?? route.base;

    // Passos: revela só os da rota e ordena.
    /** @type {HTMLElement[]} */
    const items = [];
    for (const el of this.querySelectorAll('[data-step-card]')) {
      const card = /** @type {HTMLElement} */ (el);
      const match = steps.findIndex((s) => s.line === card.dataset.line && s.step === card.dataset.step);
      card.hidden = match === -1;
      if (match > -1) {
        // Rede de segurança: se dois passos resolverem para o MESMO produto
        // (ex.: o mesmo sérum finalizando e protegendo do calor), mostra só o
        // primeiro — senão a cliente vê o item duplicado e a barra soma duas vezes.
        const id = card.dataset.itemId ?? '';
        if (id && items.some((el) => el.dataset.itemId === id)) {
          card.hidden = true;
          continue;
        }
        card.style.order = String(match);
        const n = card.querySelector('[data-step-n]');
        if (n) n.textContent = String(match + 1);
        items.push(card);
      }
    }

    // Kit em destaque: da linha que domina a rotina.
    for (const el of this.querySelectorAll('[data-kit-card]')) {
      const kit = /** @type {HTMLElement} */ (el);
      kit.hidden = kit.dataset.line !== dominant;
    }

    // Copy do resultado (headline, porquê, badge, accent) da linha dominante.
    for (const el of this.querySelectorAll('[data-result-copy]')) {
      const copy = /** @type {HTMLElement} */ (el);
      copy.hidden = copy.dataset.line !== dominant;
    }

    const accent = this.querySelector(`[data-result-copy][data-line="${dominant}"]`);
    if (accent instanceof HTMLElement) {
      if (accent.dataset.accent) this.style.setProperty('--gq-accent', accent.dataset.accent);
      // A tinta vem calculada do Liquid — accents claros (Aqua, Areia) precisam
      // de texto escuro ou o badge fica ilegível.
      if (accent.dataset.accentInk) this.style.setProperty('--gq-accent-ink', accent.dataset.accentInk);
    }

    // O nome da rota lista as linhas que REALMENTE entraram na rotina, na ordem
    // dos passos — não os boosters abstratos. Anunciar uma linha que não aparece
    // em nenhum passo quebra a confiança na recomendação.
    const routeName = this.refs.routeName;
    if (routeName instanceof HTMLElement) {
      // Base primeiro: "Linha Cachos + África Baobá" lê melhor que o inverso,
      // mesmo quando o booster é quem assume o passo de lavagem.
      const used = [...new Set(steps.map((s) => s.line))].sort((a, b) =>
        a === route.base ? -1 : b === route.base ? 1 : 0
      );
      routeName.textContent = used.map((k) => this.#lineName(k)).join(' + ');
    }

    // Disclaimer dermatológico é obrigatório quando o couro cabeludo entra na rota.
    const care = this.refs.careDisclaimer;
    if (care instanceof HTMLElement) care.hidden = !route.boosters.includes('scalpcare');

    this.#refreshBar();
    this.#trackItemList(route, items);
  }

  /**
   * @param {string} key
   * @returns {string}
   */
  #lineName(key) {
    const el = this.querySelector(`[data-result-copy][data-line="${key}"]`);
    return (el instanceof HTMLElement && el.dataset.name) || key;
  }

  /**
   * Dispara `view_item_list` canônico do GA4 — é o que faz o add-to-cart do
   * resultado atribuir corretamente ao quiz.
   *
   * @param {Route} route
   * @param {HTMLElement[]} items
   */
  #trackItemList(route, items) {
    if (!items.length) return;

    track(
      'view_item_list',
      {
        item_list_id: `quiz_result_${route.key}`,
        item_list_name: `Quiz — rotina ${route.key}`,
        items: items.map((el, index) => ({
          item_id: el.dataset.itemId ?? '',
          item_name: el.dataset.itemName ?? '',
          price: toMajorUnits(el.dataset.itemPrice),
          index,
          item_list_id: `quiz_result_${route.key}`,
        })),
      },
      { dedupKey: `quiz_list:${route.key}` }
    );
  }

  // ------------------------------------------------------- barra de compra

  /**
   * Único ponto de escrita no carrinho. Usa `/cart/add.js` e o `CartUpdateEvent`
   * do tema — o mesmo contrato do resto da loja, então drawer, contador e o
   * checkout Yampi seguem funcionando sem nada específico do quiz.
   *
   * @param {{id: number, quantity: number}[]} items
   */
  async #addToCart(items) {
    const response = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        items,
        sections: cartItemsSectionIds(),
        sections_url: window.location.pathname,
      }),
    });
    const payload = await response.json();
    if (!response.ok || payload.status || payload.errors) {
      throw new Error(payload.description || payload.message || 'Não foi possível adicionar ao carrinho.');
    }

    const cart = await (await fetch('/cart.js')).json();
    document.dispatchEvent(
      new CartUpdateEvent(cart, SOURCE_ID, {
        source: SOURCE_ID,
        itemCount: cart?.item_count ?? 0,
        sections: payload.sections,
      })
    );
  }

  /** @returns {HTMLElement[]} Passos visíveis e marcados para levar. */
  #selectedSteps() {
    return /** @type {HTMLElement[]} */ (
      [...this.querySelectorAll('.gq-step')].filter((el) => {
        if (!(el instanceof HTMLElement) || el.hidden) return false;
        const keep = el.querySelector('.gq-keep input');
        return keep instanceof HTMLInputElement && keep.checked;
      })
    );
  }

  /**
   * Grava uma resposta escalar. Existe para não indexar `#answers` por
   * `keyof Answers` — `incomodo2` é lista, e o índice genérico faz o checkJs
   * exigir `string & string[]`.
   *
   * @param {string} name
   * @param {string} value
   */
  #setAnswer(name, value) {
    if (name === 'incomodo2') return;
    this.#answers[/** @type {'curvatura'} */ (name)] = value;
  }

  /** Atualiza contagem, total e estado do botão da barra fixa. */
  #refreshBar() {
    const { bar, barCount, barTotal, barButton } = this.refs;
    if (!(bar instanceof HTMLElement)) return;

    const steps = this.#selectedSteps();
    // Produtos com mais de uma variante não entram no multi-add: escolher a
    // variante errada por ela seria pior que deixar o botão individual resolver.
    const addable = steps.filter((el) => el.dataset.variantId);
    const total = addable.reduce((sum, el) => sum + Number(el.dataset.itemPrice ?? 0), 0);

    bar.hidden = steps.length === 0;
    if (barCount instanceof HTMLElement) {
      // Conta o que o botão realmente adiciona. Contar `steps` e somar
      // `addable` mostrava "5 itens" com o total de 4 quando um passo estava
      // esgotado ou tinha mais de uma variante.
      const n = addable.length;
      let texto = n === 1 ? '1 item na rotina' : `${n} itens na rotina`;
      if (addable.length !== steps.length) {
        texto += ` · ${steps.length - addable.length} para escolher à parte`;
      }
      barCount.textContent = texto;
    }
    if (barTotal instanceof HTMLElement) barTotal.textContent = this.#money(total);
    if (barButton instanceof HTMLButtonElement) barButton.disabled = addable.length === 0;
  }

  /**
   * Formata centavos em BRL usando a config de moeda da loja.
   *
   * @param {number} cents
   */
  #money(cents) {
    const currency = /** @type {any} */ (window).Shopify?.currency?.active || 'BRL';
    // toMajorUnits respeita a precisão real da moeda — assets/analytics-schema.js
    // proíbe explicitamente hardcodar /100.
    const amount = toMajorUnits(cents, currency);
    try {
      return new Intl.NumberFormat('pt-BR', { style: 'currency', currency }).format(amount);
    } catch {
      return `R$ ${amount.toFixed(2).replace('.', ',')}`;
    }
  }

  /**
   * Adiciona todos os passos marcados de uma vez.
   *
   * Usa /cart/add.js com `items[]`, o mesmo endpoint do resto do tema. O Yampi
   * só age na página de carrinho, então o checkout não é afetado por isto.
   *
   * @type {() => Promise<void>}
   */
  addAll = async () => {
    const { barButton, barMsg } = this.refs;
    const steps = this.#selectedSteps().filter((el) => el.dataset.variantId);
    if (!steps.length) return;

    if (barButton instanceof HTMLButtonElement) {
      barButton.disabled = true;
      barButton.setAttribute('aria-busy', 'true');
    }
    if (barMsg instanceof HTMLElement) barMsg.textContent = '';

    try {
      await this.#addToCart(steps.map((el) => ({ id: Number(el.dataset.variantId), quantity: 1 })));

      // NAO emitir `add_to_cart` aqui. `#addToCart` despacha CartUpdateEvent, e o diff do carrinho
      // em analytics-subscribers ja emite o evento — com payload mais rico (nome, preco, sku e
      // variante lidos da linha do carrinho) e com `atc_source: quiz_bundle`, que vem do
      // `data-atc-source` no botao. Emitir aqui tambem contava cada adicao do quiz DUAS vezes, com
      // dois nomes de prop diferentes (`source` aqui, `atc_source` la): 163 eventos na Apice em 24h.

      if (barMsg instanceof HTMLElement) {
        barMsg.textContent = `Rotina adicionada — ${steps.length} ${steps.length === 1 ? 'item' : 'itens'} no carrinho.`;
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Não foi possível adicionar a rotina.';
      if (barMsg instanceof HTMLElement) barMsg.textContent = message;
      document.dispatchEvent(new CartErrorEvent(SOURCE_ID, message, 'Erro no quiz', {}));
    } finally {
      if (barButton instanceof HTMLButtonElement) {
        barButton.disabled = false;
        barButton.removeAttribute('aria-busy');
      }
    }
  };

  /**
   * Adiciona um único produto — mesmo endpoint e mesmo evento do `addAll`.
   *
   * @param {Event} event
   */
  addOne = async (event) => {
    const button = /** @type {HTMLElement} */ (event.target).closest('.gq-buy');
    if (!(button instanceof HTMLButtonElement) || !button.dataset.variantId) return;

    button.setAttribute('aria-busy', 'true');
    // O rótulo vive num <span> ao lado do ícone; trocar o textContent do botão
    // apagaria o ícone. Guardado em data-label para não acumular ao reclicar.
    const label = button.querySelector('span') ?? button;
    if (!button.dataset.label) button.dataset.label = label.textContent?.trim() ?? 'Adicionar';

    try {
      await this.#addToCart([{ id: Number(button.dataset.variantId), quantity: 1 }]);
      // Ver a nota em `addAll`: o diff do carrinho emite o `add_to_cart`, atribuido por
      // `data-atc-source="quiz_step"` no botao. Emitir aqui duplicaria a contagem.
      button.classList.add('is-added');
      label.textContent = 'Adicionado';
      window.clearTimeout(Number(button.dataset.resetTimer));
      button.dataset.resetTimer = String(
        window.setTimeout(() => {
          button.classList.remove('is-added');
          label.textContent = button.dataset.label ?? 'Adicionar';
        }, 2200)
      );
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Não foi possível adicionar.';
      document.dispatchEvent(new CartErrorEvent(SOURCE_ID, message, 'Erro no quiz', {}));
    } finally {
      button.removeAttribute('aria-busy');
    }
  };

  // ------------------------------------------------------------- e-mail

  /**
   * Envia o e-mail opcional sem tirar a cliente da página.
   *
   * O form nativo `customer` do Shopify é a única via de tema para criar um
   * contato, e `contact[tags]` é o que aplica a tag. Falha silenciosa de
   * propósito: e-mail é opcional e não pode impedir ninguém de fazer o quiz.
   */
  async #submitEmail() {
    const { email, signup, consent } = this.refs;
    if (this.#emailSent) return;
    if (!(email instanceof HTMLInputElement) || !email.value.trim() || !email.checkValidity()) return;

    // O aviso de caráter informativo deixa de ser decorativo: desmarcado, o
    // e-mail não é enviado.
    if (consent instanceof HTMLInputElement && !consent.checked) return;

    const form = signup instanceof HTMLElement ? signup.querySelector('form') : null;
    if (!(form instanceof HTMLFormElement)) return;

    try {
      const response = await fetch(form.action, {
        method: 'POST',
        body: new FormData(form),
        headers: { Accept: 'text/html' },
      });
      if (!response.ok) throw new Error(String(response.status));
      this.#emailSent = true;
      email.disabled = true;
      track('generate_lead', { params: { source: 'quiz', tag: 'quiz' } }, { dedupKey: 'quiz_lead' });
    } catch {
      // Falhou: mantém o campo editável para a cliente tentar de novo, e NÃO
      // reporta lead que não existe.
      email.disabled = false;
    }
  }

  // ------------------------------------------------------------ persistência

  #persist() {
    try {
      sessionStorage.setItem('goshop-quiz', JSON.stringify(this.#answers));
    } catch {
      /* modo privado / storage cheio — o quiz segue funcionando sem persistir */
    }
  }

  #restore() {
    try {
      const raw = sessionStorage.getItem('goshop-quiz');
      if (!raw) return;
      this.#answers = JSON.parse(raw);
      for (const [name, value] of Object.entries(this.#answers)) {
        for (const v of Array.isArray(value) ? value : [value]) {
          const input = this.querySelector(`input[name="${name}"][value="${v}"]`);
          if (input instanceof HTMLInputElement) input.checked = true;
        }
      }
    } catch {
      this.#answers = {};
    }
  }
}

if (!customElements.get('goshop-quiz')) {
  customElements.define('goshop-quiz', GoshopQuiz);
}

// Exportadas para o script de sanidade das rotas (scripts/quiz-routes-check.mjs),
// que percorre todas as combinações de respostas e falha se alguma resolver para
// um passo vazio. É a única verificação automatizada deste escopo.
export { resolveRoute, resolveSteps, resolveBoosters };
